import { request } from '@umijs/max';
import authService from '@/services/AuthService';
import type { SimplifiedPendingOrder } from '@/services/PendingOrdersService';

export interface OrderEmailRecipient {
  id: number;
  name: string;
  email: string;
}

export interface OrderEmailPreferences {
  order_type?: string;
  to_emails: string[];
  cc_emails: string[];
  bcc_emails: string[];
  custom_message: string;
}

export interface SendOrderEmailPayload {
  order: SimplifiedPendingOrder;
  pdfBase64: string;
  filename: string;
  toEmails: string[];
  ccEmails?: string[];
  bccEmails?: string[];
  customMessage?: string;
  subject?: string;
}

class OrderEmailService {
  private readonly API_BASE_URL: string;

  constructor() {
    this.API_BASE_URL =
      process.env.UMI_APP_API_URL || (window as any).__UMI_ENV__?.UMI_APP_API_URL || 'http://localhost:1337';
  }

  private buildUrl(path: string): string {
    return `${this.API_BASE_URL.replace(/\/$/, '')}${path}`;
  }

  private authHeaders() {
    return {
      'Content-Type': 'application/json',
      ...authService.getAuthHeader(),
    };
  }

  async getRecipients(): Promise<OrderEmailRecipient[]> {
    const response = await request<{ data: OrderEmailRecipient[] }>(this.buildUrl('/order-email/recipients'), {
      method: 'GET',
      headers: this.authHeaders(),
      skipErrorHandler: true,
    });
    return response?.data || [];
  }

  async getPreferences(orderType?: string): Promise<OrderEmailPreferences> {
    const response = await request<{ data: OrderEmailPreferences }>(this.buildUrl('/order-email/preferences'), {
      method: 'GET',
      params: { order_type: orderType },
      headers: this.authHeaders(),
      skipErrorHandler: true,
    });
    return (
      response?.data || {
        to_emails: [],
        cc_emails: [],
        bcc_emails: [],
        custom_message: '',
      }
    );
  }

  async updatePreferences(preferences: OrderEmailPreferences): Promise<OrderEmailPreferences> {
    const response = await request<{ data: OrderEmailPreferences }>(this.buildUrl('/order-email/preferences'), {
      method: 'PUT',
      headers: this.authHeaders(),
      data: preferences,
      skipErrorHandler: true,
    });
    return (
      response?.data || {
        to_emails: [],
        cc_emails: [],
        bcc_emails: [],
        custom_message: '',
      }
    );
  }

  async sendOrderEmail(payload: SendOrderEmailPayload): Promise<void> {
    await request(this.buildUrl('/order-email/send'), {
      method: 'POST',
      headers: this.authHeaders(),
      data: {
        order: payload.order,
        pdf_base64: payload.pdfBase64,
        filename: payload.filename,
        to_emails: payload.toEmails,
        cc_emails: payload.ccEmails || [],
        bcc_emails: payload.bccEmails || [],
        custom_message: payload.customMessage || '',
        subject: payload.subject || '',
      },
      skipErrorHandler: true,
    });
  }
}

export default new OrderEmailService();

